/**
 * src/lib/hooks/useSlashCommands.js
 *
 * Phase 82-04 — Chat System Unification.
 *
 * Parses the current composer text to detect an active slash trigger and
 * returns the filtered command list + keyboard-nav cursor. Keeps the trigger
 * behaviour identical between the slash menu and the /cmd autocomplete.
 *
 * Rules:
 *   - Trigger fires when "/" is the first char and the following token has
 *     no whitespace (i.e. we're still mid-command).
 *   - onSelect(cmd) is the commit path — the consumer decides whether to
 *     dispatch as a tool or run a local handler.
 *   - Escape closes the menu without clearing the input.
 *
 * Hooks at top of body — Phase 90-07 rule.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { filter as filterCommands } from '../agent/slashCommandRegistry';

function parseSlashQuery(text, caret) {
  if (typeof text !== 'string') return null;
  const cursor = Number.isInteger(caret) ? caret : text.length;
  const before = text.slice(0, cursor);
  if (!before.startsWith('/')) return null;
  const token = before.slice(1);
  if (/\s/.test(token)) return null;
  return token;
}

export function useSlashCommands({ text, caret, enabled = true } = {}) {
  const query = enabled ? parseSlashQuery(text, caret) : null;
  const open = query !== null;
  const [cursor, setCursor] = useState(0);

  const results = useMemo(() => {
    if (!open) return [];
    return filterCommands(query);
  }, [open, query]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  useEffect(() => {
    if (cursor >= results.length) setCursor(Math.max(0, results.length - 1));
  }, [cursor, results.length]);

  const move = useCallback((delta) => {
    setCursor((prev) => {
      const n = results.length;
      if (n === 0) return 0;
      return (prev + delta + n) % n;
    });
  }, [results.length]);

  const getHighlighted = useCallback(() => results[cursor] || null, [results, cursor]);

  const onKeyDown = useCallback((e, onSelect, onClose) => {
    if (!open) return false;
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); return true; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); move(-1); return true; }
    if (e.key === 'Enter' || e.key === 'Tab') {
      const cmd = getHighlighted();
      if (cmd) {
        e.preventDefault();
        onSelect?.(cmd);
        return true;
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
      return true;
    }
    return false;
  }, [open, move, getHighlighted]);

  return {
    open,
    query: query || '',
    results,
    cursor,
    setCursor,
    move,
    getHighlighted,
    onKeyDown,
  };
}

export default useSlashCommands;
